/**
 * Хранилище состояния редактора (zustand): инструмент, выделение, раскладка, настройки сетки.
 * Любой виджет или фича может подписаться на нужный кусок, не таща за собой весь чертёж.
 */
import { create } from "zustand";
import {
	EXTERIOR_WALL_THICKNESS,
	CatalogSectionKind,
	OPENING_PRESET,
	WALL_DEFAULTS,
	type CatalogRef,
	type CommentTargetKind,
	type OpeningKind,
} from "@/shared/plan";
import { DEFAULT_WALL_MATERIAL, WallMaterial } from "@/shared/catalog";
import {
	WaterMedium,
	defaultPipe,
	firstCatalogRef,
	type CatalogSectionKey,
} from "@/shared/catalog";
import { LocalStorageUtils } from "@/shared/lib/storage";
import { GRID_STEP } from "../config/units";
import {
	SelectionKind,
	Tool,
	ViewMode,
	type DrawerState,
	type PlanStage,
	type Selection,
} from "./editorTypes";
import {
	drawerFeature,
	planStageAllowsDrawer,
	planStageAllowsTool,
	toolFeature,
	type PlanStageFeature,
} from "./planStages";

export { DEFAULT_WALL_MATERIAL, EXTERIOR_WALL_THICKNESS, OPENING_PRESET, WALL_DEFAULTS };
/** Размер комнаты в чистоте, мм: ширина — по горизонтали плана, глубина — по вертикали. */
export interface RoomSize {
	width: number;
	depth: number;
}

export type { CatalogRef, WallMaterial };

/**
 * Тип стены — это выбор инструмента, а не параметр: «Перегородка» и «Несущая стена»
 * живут отдельными кнопками, и у каждой своя память толщины, высоты и материала.
 */
export type WallRole = "partition" | "bearing";

/**
 * Как чертим стены — как «Геометрический вариант» в ArchiCAD:
 *   • chain — цепочка: стена за стеной, пока не закончишь;
 *   • single — одна стена: две точки, и черчение начинается заново;
 *   • rectangle — прямоугольник по диагонали: два противоположных угла;
 *   • rotated — повёрнутый прямоугольник: первая стена под любым углом, потом ширина.
 * Прямоугольники — это сразу комната: четыре стены одним шагом истории.
 */
export type WallMethod = "chain" | "single" | "rectangle" | "rotated";

/** Параметры, с которыми создаются новые стены. */
export interface WallSettings {
	thickness: number;
	height: number;
	material: WallMaterial;
	/** Рисовать несущие стены — выводится из выбранного типа стены */
	bearing: boolean;
}

type WallMemory = Omit<WallSettings, "bearing">;

/** С чего начинает каждый тип стены. Правки пользователя запоминаются поверх. */ export const BEARING_WALL_DEFAULTS =
	{
		thickness: EXTERIOR_WALL_THICKNESS,
		material: WallMaterial.Brick,
	} as const;
const WALL_ROLE_DEFAULTS: Record<WallRole, WallMemory> = {
	partition: {
		thickness: WALL_DEFAULTS.thickness,
		height: WALL_DEFAULTS.height,
		material: DEFAULT_WALL_MATERIAL,
	},
	bearing: {
		thickness: BEARING_WALL_DEFAULTS.thickness,
		height: WALL_DEFAULTS.height,
		material: BEARING_WALL_DEFAULTS.material,
	},
};

const settingsFor = (role: WallRole, memory: Record<WallRole, WallMemory>): WallSettings => ({
	...memory[role],
	bearing: role === "bearing",
});

/** Что показывать в 3D-виде. Живёт в сторе редактора, а не в самой сцене,
 *  чтобы настройки не сбрасывались при переключении 2D ⇄ 3D. */
export interface View3DSettings {
	/** Растворять стены между камерой и центром квартиры, чтобы видеть комнаты изнутри */
	cutaway: boolean;
	/** Сетка участка под домом */
	grid: boolean;
	/** Карта помещений поверх 3D-сцены */
	roomMap: boolean;
	/** Круг выбора направления солнца */
	sunControl: boolean;
	/** Экранные стрелки движения камеры */
	cameraMovePad: boolean;
	/** Камера на высоте глаз: обзор мышью и перемещение по квартире */
	firstPerson: boolean;
}

const isCompact3dLayout = () =>
	typeof window !== "undefined" && window.matchMedia("(max-width: 1023px)").matches;

const defaultView3dSettings = (): View3DSettings => {
	const compact = isCompact3dLayout();
	return {
		cutaway: false,
		grid: true,
		roomMap: !compact,
		sunControl: true,
		cameraMovePad: true,
		firstPerson: false,
	};
};

/**
 * Этап, по которому решают, что сейчас доступно. Этапы — это 2D-чертёж: в 3D и на развёртках
 * каталог и инструменты работают при любом выбранном этапе.
 */
const effectiveStage = (s: { viewMode: ViewMode; planStage: PlanStage }): PlanStage =>
	s.viewMode === ViewMode.TwoD || s.viewMode === ViewMode.Split ? s.planStage : "all";

/** К какому разделу левой панели относится выделенный объект; undefined — ни к какому (комментарий). */
const SELECTION_FEATURE: Partial<Record<SelectionKind, PlanStageFeature>> = {
	[SelectionKind.Wall]: "walls",
	[SelectionKind.Node]: "walls",
	[SelectionKind.Opening]: "openings",
	[SelectionKind.Fixture]: "catalog",
	[SelectionKind.Item]: "catalog",
	[SelectionKind.Light]: "catalog",
	[SelectionKind.Cable]: "wiring",
	[SelectionKind.LightingConnection]: "wiring",
	[SelectionKind.Pipe]: "water",
};

/** Чем кончилось сохранение: ушло на сервер или пока лежит только в браузере. */
export type ProjectSaveResult = "synced" | "local";

/** Параметры, с которыми прокладываются новые трубы: среда и материал из раздела «Труба». */
export interface PipeSettings {
	medium: WaterMedium;
	/** Id материала в каталоге труб */
	material: string;
}

/** Как рисовать вспомогательные связи между светильниками и настенными приборами. */
export type LightingConnectionStyle = "arc" | "line";

/** Параметры, с которыми создаются новые двери и окна. */
export interface OpeningSettings {
	door: { item: string; width: number; height: number; sill: number };
	window: { item: string; width: number; height: number; sill: number };
}

interface EditorState {
	tool: Tool;
	selection: Selection;
	/** Открытый общий проект без права изменения. */
	isReadOnly: boolean;
	/**
	 * Сохранить открытый серверный проект сейчас, не дожидаясь автосохранения.
	 * Ставит синхронизация проекта; null — проект не с сервера.
	 */
	saveProject: (() => Promise<ProjectSaveResult>) | null;
	viewMode: ViewMode;
	/** Активный этап 2D-чертежа (Исходный план, Демонтаж, Монтаж, Мебель, Пол и т.д.). */
	planStage: PlanStage;
	/** Открытый раздел левой панели инструментов */
	drawer: DrawerState;
	/** Левая панель широкая: подписи инструментов справа от иконок. Помнит браузер. */
	isPlannerLeftPanelWide: boolean;
	/** Отступ экранных оверлеев от верхней панели редактора. */
	overlayTopOffset: number;
	/** Какой тип стены рисуем: перегородку или несущую */
	wallRole: WallRole;
	/** Способ черчения стен: цепочка, одна стена, прямоугольник или повёрнутый прямоугольник */
	wallMethod: WallMethod;
	/** Параметры текущего типа стены — их правит панель инструментов */
	wallSettings: WallSettings;
	/** Параметры каждого типа, чтобы переключение туда-обратно ничего не теряло */
	wallMemory: Record<WallRole, WallMemory>;
	openingSettings: OpeningSettings;
	/** Среда и материал следующей трубы — их правит панель «Вода» */
	pipeSettings: PipeSettings;
	cableMaterial: string | null;
	cableGroupId: string | null;
	/** Какой объект каталога ставится следующим инструментом `place` */
	placing: CatalogRef | null;
	/** Размер комнаты для инструмента «Комната» — в чистоте, от стены до стены, мм */
	roomSize: RoomSize;
	/**
	 * Карточку каталога тащат на чертёж или в 3D: что тащат. Пока не null, `placing` — тот же
	 * тип, за курсором едет призрак, а холст ставит объект там, где кнопку отпустили.
	 */
	catalogDrag: CatalogRef | null;
	/** Дверь или окно тащат из раздела «Проёмы»: отпустили над стеной — проём встаёт в эту точку */
	openingDrag: OpeningKind | null;
	/**
	 * Панель свойств просит холст добавить комментарий к выделенному объекту: холст
	 * ставит выноску рядом с объектом, открывает окошко текста и сбрасывает запрос.
	 */
	commentFor: { kind: CommentTargetKind; id: string } | null;
	/** Раздел каталога, открытый в панели инструментов */
	activeCatalog: CatalogSectionKey;
	setActiveCatalog: (key: CatalogSectionKey) => void;
	/** Комната, чью раскладку пола сейчас двигают на плане. */
	movingFloorPatternRoomId: string | null;
	setMovingFloorPatternRoomId: (roomId: string | null) => void;
	/** Помещение, чьи развёртки показаны. null — первое найденное */
	elevationRoomId: string | null;
	/** Показывать карту помещений поверх развёрток. */
	showElevationRoomMap: boolean;
	view3d: View3DSettings;
	/** Счётчик запросов на возврат 3D-камеры к обзору всего плана. */
	view3dResetToken: number;
	/** Показывать подписи длин стен */
	showDimensions: boolean;
	/** Показывать площади комнат */
	showAreas: boolean;
	/** Показывать вручную заданные связи выключателей со светильниками. */
	showLightingConnections: boolean;
	/** Форма следующей связи светильника с настенным электроприбором. */
	lightingConnectionStyle: LightingConnectionStyle;
	/** Шаг привязки, мм */
	gridStep: number;
	/** Текущий этап генерации PDF; блокирует редактор на время временного переключения слоёв. */
	pdfExportProgress: string | null;
	/**
	 * Счётчик «начать черчение заново». Растёт при выборе инструмента кнопкой
	 * или клавишей — незаконченная цепочка стен при этом сбрасывается, чтобы новая
	 * стена не продолжалась из старой точки. Tab посреди черчения его не трогает.
	 */
	drawResetToken: number;

	setTool: (tool: Tool) => void;
	setReadOnly: (isReadOnly: boolean) => void;
	setSaveProject: (saveProject: (() => Promise<ProjectSaveResult>) | null) => void;
	setDrawer: (drawer: DrawerState) => void;
	togglePlannerLeftPanelWide: () => void;
	setOverlayTopOffset: (offset: number) => void;
	setWallSettings: (patch: Partial<WallMemory>) => void;
	/** Выбирает тип стены и включает инструмент стены */
	setWallRole: (role: WallRole) => void;
	/** Выбирает способ черчения и включает инструмент стены; начатый черновик сбрасывается */
	setWallMethod: (method: WallMethod) => void;
	/** Перегородка ⇄ несущая — для горячей клавиши прямо во время черчения */
	toggleWallRole: () => void;
	setOpeningSettings: (kind: "door" | "window", patch: Partial<OpeningSettings["door"]>) => void;
	setPipeSettings: (patch: Partial<PipeSettings>) => void;
	setCableMaterial: (material: string | null) => void;
	setCableGroupId: (groupId: string | null) => void;
	setPlanStage: (planStage: PlanStage) => void;
	setSelection: (selection: Selection) => void;
	setViewMode: (viewMode: ViewMode) => void;
	/** Выбирает объект каталога и включает инструмент установки */
	setPlacing: (ref: CatalogRef) => void;
	/** Начать перетаскивание карточки: выбирает тип, как клик по карточке */
	startCatalogDrag: (ref: CatalogRef) => void;
	/** Перетаскивание закончено; `cancel` — отпустили мимо: установка отменяется */
	endCatalogDrag: (cancel: boolean) => void;
	/** Начать перетаскивание двери или окна: включает их инструмент, как клик по кнопке */
	startOpeningDrag: (kind: OpeningKind) => void;
	/** Проём отпустили; `cancel` — мимо стены: ничего не ставится */
	endOpeningDrag: (cancel: boolean) => void;
	setCommentFor: (commentFor: { kind: CommentTargetKind; id: string } | null) => void;
	setRoomSize: (patch: Partial<RoomSize>) => void;
	setElevationRoomId: (elevationRoomId: string | null) => void;
	toggleElevationRoomMap: () => void;
	setView3d: (patch: Partial<View3DSettings>) => void;
	resetView3d: () => void;
	setGridStep: (gridStep: number) => void;
	setPdfExportProgress: (pdfExportProgress: string | null) => void;
	toggleDimensions: () => void;
	toggleAreas: () => void;
	toggleLightingConnections: () => void;
	setLightingConnectionStyle: (style: LightingConnectionStyle) => void;
}

export const useEditorStore = create<EditorState>((set) => ({
	tool: Tool.Select,
	selection: null,
	isReadOnly: false,
	saveProject: null,
	planStage: "all",
	viewMode: ViewMode.TwoD,
	drawer: null,
	// По умолчанию левая панель раскрыта; выбор пользователя помнит браузер
	isPlannerLeftPanelWide: LocalStorageUtils.isPlannerLeftPanelWide(),
	overlayTopOffset: 0,
	wallRole: "partition",
	wallMethod: "chain",
	wallSettings: settingsFor("partition", WALL_ROLE_DEFAULTS),
	wallMemory: WALL_ROLE_DEFAULTS,
	openingSettings: {
		door: { ...OPENING_PRESET.door },
		window: { ...OPENING_PRESET.window },
	},
	pipeSettings: { medium: WaterMedium.Cold, material: defaultPipe().id },
	cableMaterial: null,
	cableGroupId: null,
	placing: firstCatalogRef(),
	roomSize: { width: 3000, depth: 2000 },
	catalogDrag: null,
	openingDrag: null,
	commentFor: null,
	// Каталог открывается на мебели — её ищут чаще всего; этапы чертежа открывают свой раздел
	activeCatalog: CatalogSectionKind.Furniture,
	movingFloorPatternRoomId: null,
	elevationRoomId: null,
	showElevationRoomMap: !isCompact3dLayout(),
	view3d: defaultView3dSettings(),
	view3dResetToken: 0,
	showDimensions: true,
	showAreas: true,
	showLightingConnections: false,
	lightingConnectionStyle: "arc",
	gridStep: GRID_STEP,
	pdfExportProgress: null,
	drawResetToken: 0,

	// Смена инструмента сбрасывает выделение: панель свойств не должна показывать
	// объект, с которым пользователь уже не работает.
	setTool: (tool) =>
		set((s) => ({
			// В просмотре и на этапе, где инструменту нечего делать (кабель на «Мебели»), — только выбор
			...((s.isReadOnly && tool !== Tool.Select) || !planStageAllowsTool(effectiveStage(s), tool)
				? {}
				: { tool }),
			selection: tool === Tool.Select ? s.selection : null,
			movingFloorPatternRoomId: tool === Tool.Select ? s.movingFloorPatternRoomId : null,
			drawResetToken: s.drawResetToken + 1,
		})),
	setSaveProject: (saveProject) => set({ saveProject }),
	setReadOnly: (isReadOnly) =>
		set({
			isReadOnly,
			selection: null,
			tool: Tool.Select,
			catalogDrag: null,
			openingDrag: null,
			commentFor: null,
			movingFloorPatternRoomId: null,
			...(isReadOnly ? { drawer: null } : {}),
		}),
	setSelection: (selection) =>
		set((s) =>
			s.isReadOnly && selection !== null
				? {}
				: {
						selection,
						movingFloorPatternRoomId:
							selection?.kind === SelectionKind.Room && selection.id === s.movingFloorPatternRoomId
								? s.movingFloorPatternRoomId
								: null,
					},
		),
	// Перешли в другой раздел — инструмент и выделение прошлого раздела больше не действуют:
	// стену не дочерчивают из «Воды», а выделенная стена не висит в свойствах. Закрытая панель
	// и разделы без своих инструментов («Скрыть», «Инфо», «Отделка») ничего не сбрасывают
	setDrawer: (drawer) =>
		set((s) => {
			if (!planStageAllowsDrawer(effectiveStage(s), drawer)) return {};
			if (drawer === s.drawer) return { drawer };
			const section = drawerFeature(drawer);
			const currentTool = toolFeature(s.tool);
			// Закрыли панель или открыли раздел без инструментов — инструмент остаётся: предмет
			// из каталога ставят и при закрытой панели
			const keepTool =
				section === undefined || currentTool === undefined || currentTool === section;
			const selected = s.selection ? SELECTION_FEATURE[s.selection.kind] : undefined;
			const keepSelection = section === undefined || selected === undefined || selected === section;
			return {
				drawer,
				...(keepTool
					? {}
					: {
							tool: Tool.Select,
							catalogDrag: null,
							openingDrag: null,
							drawResetToken: s.drawResetToken + 1,
						}),
				...(keepSelection ? {} : { selection: null, movingFloorPatternRoomId: null }),
			};
		}),
	togglePlannerLeftPanelWide: () =>
		set((state) => {
			const isPlannerLeftPanelWide = !state.isPlannerLeftPanelWide;
			LocalStorageUtils.setPlannerLeftPanelWide(isPlannerLeftPanelWide);
			return { isPlannerLeftPanelWide };
		}),
	setOverlayTopOffset: (overlayTopOffset) => set({ overlayTopOffset }),
	// Правка параметров сразу ложится в память текущего типа стены
	setWallSettings: (patch) =>
		set((s) => {
			const memory = { ...s.wallMemory, [s.wallRole]: { ...s.wallMemory[s.wallRole], ...patch } };
			return { wallMemory: memory, wallSettings: settingsFor(s.wallRole, memory) };
		}),
	setWallRole: (role) =>
		set((s) => ({
			...(s.isReadOnly ? {} : { tool: Tool.Wall }),
			wallRole: role,
			wallSettings: settingsFor(role, s.wallMemory),
			selection: null,
			drawResetToken: s.drawResetToken + 1,
		})),
	setWallMethod: (method) =>
		set((s) => ({
			...(s.isReadOnly ? {} : { tool: Tool.Wall }),
			wallMethod: method,
			selection: null,
			drawResetToken: s.drawResetToken + 1,
		})),
	toggleWallRole: () =>
		set((s) => {
			const role: WallRole = s.wallRole === "bearing" ? "partition" : "bearing";
			return { wallRole: role, wallSettings: settingsFor(role, s.wallMemory) };
		}),
	setOpeningSettings: (kind, patch) =>
		set((s) => ({
			openingSettings: { ...s.openingSettings, [kind]: { ...s.openingSettings[kind], ...patch } },
		})),
	setPipeSettings: (patch) => set((s) => ({ pipeSettings: { ...s.pipeSettings, ...patch } })),
	setCableMaterial: (cableMaterial) => set({ cableMaterial }),
	setCableGroupId: (cableGroupId) => set({ cableGroupId }),
	// Режим меняет и набор инструментов: в развёртках чертить стены нечем,
	// а в чертеже нет розеток.
	setViewMode: (viewMode) => {
		set((state) => ({
			viewMode,
			selection: null,
			movingFloorPatternRoomId: null,
			tool: Tool.Select,
			drawer: null,
			view3d: viewMode === ViewMode.ThreeD ? state.view3d : { ...state.view3d, firstPerson: false },
		}));
	},
	setPlanStage: (planStage) =>
		set((state) => {
			const stage = effectiveStage({ viewMode: state.viewMode, planStage });
			return {
				planStage,
				// Раздел и инструмент, которых на новом этапе нет, закрываем
				drawer: planStageAllowsDrawer(stage, state.drawer) ? state.drawer : null,
				tool: planStageAllowsTool(stage, state.tool) ? state.tool : Tool.Select,
				catalogDrag: planStageAllowsTool(stage, Tool.Place) ? state.catalogDrag : null,
				selection: null,
				movingFloorPatternRoomId: null,
				drawResetToken: state.drawResetToken + 1,
			};
		}),
	setPlacing: (placing) =>
		set((s) =>
			s.isReadOnly || !planStageAllowsTool(effectiveStage(s), Tool.Place)
				? {}
				: { placing, tool: Tool.Place, selection: null, movingFloorPatternRoomId: null },
		),
	startCatalogDrag: (ref) =>
		set((s) =>
			s.isReadOnly || !planStageAllowsTool(effectiveStage(s), Tool.Place)
				? {}
				: {
						catalogDrag: ref,
						placing: ref,
						tool: Tool.Place,
						selection: null,
						movingFloorPatternRoomId: null,
					},
		),
	setCommentFor: (commentFor) => set((s) => (s.isReadOnly ? {} : { commentFor })),
	setRoomSize: (patch) => set((s) => ({ roomSize: { ...s.roomSize, ...patch } })),
	endCatalogDrag: (cancel) =>
		set((s) => ({
			catalogDrag: null,
			tool: cancel && s.tool === Tool.Place ? Tool.Select : s.tool,
		})),
	startOpeningDrag: (kind) =>
		set((s) => {
			const tool = kind === "door" ? Tool.Door : Tool.Window;
			return s.isReadOnly || !planStageAllowsTool(effectiveStage(s), tool)
				? {}
				: { openingDrag: kind, tool, selection: null, movingFloorPatternRoomId: null };
		}),
	endOpeningDrag: (cancel) =>
		set((s) => ({
			openingDrag: null,
			tool: cancel && (s.tool === Tool.Door || s.tool === Tool.Window) ? Tool.Select : s.tool,
		})),
	setActiveCatalog: (activeCatalog) => set({ activeCatalog }),
	// Раскладку тянут инструментом выделения: с инструментом установки клик по полу снова
	// назначил бы комнате покрытие из каталога
	setMovingFloorPatternRoomId: (movingFloorPatternRoomId) =>
		set(
			movingFloorPatternRoomId === null
				? { movingFloorPatternRoomId }
				: { movingFloorPatternRoomId, tool: Tool.Select },
		),
	setElevationRoomId: (elevationRoomId) => set({ elevationRoomId, selection: null }),
	toggleElevationRoomMap: () =>
		set((state) => ({ showElevationRoomMap: !state.showElevationRoomMap })),
	setView3d: (patch) => set((s) => ({ view3d: { ...s.view3d, ...patch } })),
	resetView3d: () => set((state) => ({ view3dResetToken: state.view3dResetToken + 1 })),
	setGridStep: (gridStep) => set({ gridStep }),
	setPdfExportProgress: (pdfExportProgress) => set({ pdfExportProgress }),
	toggleDimensions: () => set((s) => ({ showDimensions: !s.showDimensions })),
	toggleAreas: () => set((s) => ({ showAreas: !s.showAreas })),
	toggleLightingConnections: () =>
		set((s) => ({ showLightingConnections: !s.showLightingConnections })),
	setLightingConnectionStyle: (lightingConnectionStyle) => set({ lightingConnectionStyle }),
}));
